"use client";

import { useState } from "react";
import Link from "next/link";
import { getArticles } from "@/lib/content-engine";
import { ArrowRight, BookOpen, Search, X } from "lucide-react";

export default function ArticlesIndex() {
  const articles = getArticles();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredArticles = articles.filter(article => {
    const query = searchQuery.toLowerCase();
    return (
      article.title.toLowerCase().includes(query) ||
      article.excerpt.toLowerCase().includes(query) ||
      article.category.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-slate-50 pt-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6">
            Knowledge Hub
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Deep dives into AWS, GenAI, and scalable architecture.
          </p>

          {/* SEARCH BAR */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search articles..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all shadow-sm text-slate-700 placeholder:text-slate-400"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArticles.map((article) => (
            <Link 
              key={article.slug} 
              href={`/insights/${article.slug}`}
              className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all flex flex-col h-full"
            >
              {/* Image / Placeholder */}
              <div className="h-48 w-full bg-slate-100 relative flex items-center justify-center text-slate-400">
                 {article.image ? (
                   <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                 ) : (
                   <BookOpen className="h-12 w-12 opacity-20" />
                 )}
              </div>

              <div className="p-6 flex flex-col grow">
                <div className="flex items-center gap-2 mb-4 text-xs font-bold text-primary uppercase tracking-wider">
                  <span>{article.category}</span>
                  <span>•</span>
                  <span>{article.date}</span>
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                
                <p className="text-slate-600 text-sm mb-6 grow line-clamp-3">
                  {article.excerpt}
                </p>

                <div className="flex items-center text-sm font-bold text-primary mt-auto">
                  Read Article <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Empty State */}
        {filteredArticles.length === 0 && (
          <div className="text-center py-20">
             <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
             <p className="text-slate-500">No articles found matching "{searchQuery}".</p>
             <button onClick={() => setSearchQuery("")} className="mt-4 text-primary font-bold hover:underline">Clear Search</button>
          </div>
        )}
      </div>
    </div>
  );
}